import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SignupForm } from '@/components/SignupForm';
import { Logo } from '@/components/Logo';

import { useQuery } from 'hooks/query';
import { authController } from '@/states/authStateController';

import { ChainlitContext, useAuth } from '@chainlit/react-client';

export const LoginError = new Error('Error logging in. Please try again later.');

export default function Signup() {
  const query = useQuery();
  const { user, setUserFromAPI } = useAuth();
  const [error, setError] = useState('');
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();

  const handleCookieAuth = (json: any): void => {
    if (json?.success != true) throw new Error('Authentication failed');
    setUserFromAPI();
  };

  const handleAuth = async (jsonPromise: Promise<any>, redirectURL?: string) => {
    try {
      await jsonPromise;

      if (redirectURL) {
        navigate(redirectURL);
      }
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handlePasswordSignup = async (
    name: string,
    email: string,
    password: string,
    callbackUrl: string
  ) => {
    try {
      const result = await authController.signup(name, email, password);

      if (result.success) {
        // Authenticate with Chainlit after successful signup
        const formData = new FormData();
        formData.append('username', email);
        formData.append('password', password);

        try {
          const chainlitResponse = await apiClient.passwordAuth(formData);
          handleCookieAuth(chainlitResponse);
        } catch (chainlitError) {
          console.warn('Chainlit authentication failed:', chainlitError);
          setError('Signup successful but authentication failed. Please try logging in.');
          return;
        }

        // Redirect based on subscription status from context
        if (!result.user?.is_subscribed) {
          navigate('/payment');
        } else {
          navigate('/');
        }
      } else {
        setError(result.error || 'Signup failed');
      }
    } catch (error: any) {
      setError(error.message);
    }
  };

  useEffect(() => {
    setError(query.get('error') || '');
  }, [query]);

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user]);

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Logo className="w-[150px]" />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <SignupForm
              error={error}
              callbackUrl={'/login'}
              onPasswordSignUp={handlePasswordSignup}
            />
          </div>
        </div>
      </div>
      <div className="relative hidden bg-muted lg:block">
        <img
          src={apiClient.buildEndpoint('/favicon')}
          alt="Image"
          className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
        />
      </div>
    </div>
  );
}
